import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  CheckoutConfigService,
  CheckoutDeliveryModeComponent,
  CheckoutStepService,
} from '@spartacus/checkout/base/components';
import { CheckoutDeliveryModesFacade } from '@spartacus/checkout/base/root';
import { GlobalMessageService } from '@spartacus/core';
import { combineLatest, Subscription, switchMap, take } from 'rxjs';
import { filter, finalize, map, tap } from 'rxjs/operators';
import { KnBrDraftOrdersAdapter } from 'src/feature-libs/kn-br-cart/saved-carts/core/connectors/kn-br-draft-orders.adapter';
import { UserAccountFacade } from 'src/feature-libs/kn-br-user/kn-br-account/root/facade';
import { AtgCheckoutDeliveryAdapter } from './atg-checkout-delivery.adapter';

const FEDEX_GROUND_VALIDATORS: ValidatorFn[] = [
  Validators.required,
  Validators.pattern(/^[a-zA-Z0-9]{1,9}$/),
];
const DEFAULT_COLLECT_VALIDATORS: ValidatorFn[] = [
  Validators.required,
  Validators.pattern(/^[a-zA-Z0-9]{1,6}$/),
];

function normalize(value: string | null | undefined): string {
  return (value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/**
 * The backend's FedEx Ground delivery mode has been seen with varying
 * code separators/casing (e.g. FED_EX_GROUND, fedex-ground) and sometimes
 * extra prefixes/suffixes on the code. Match on "contains FEDEX and GROUND"
 * across both code and name so formatting differences don't silently fall
 * back to the generic 6-character validator.
 */
export function isFedexGround(
  code: string | null | undefined,
  name?: string | null | undefined
): boolean {
  const normalizedCode = normalize(code);
  const normalizedName = normalize(name);

  return (
    (normalizedCode.includes('FEDEX') && normalizedCode.includes('GROUND')) ||
    (normalizedName.includes('FEDEX') && normalizedName.includes('GROUND'))
  );
}

/**
 * The backend localizes the delivery mode's display `name` per storefront
 * language ("Best Way" in English, "Meilleur moyen" in Canadian French), so
 * comparing `name` against only the literal English string only matched in
 * English. `code` is a stable backend identifier that isn't translated, so
 * match on that first and also recognize the known localized name variants
 * as a fallback for environments where `code` doesn't carry the identifier.
 */
const BEST_WAY_NAME_VARIANTS = ['Best Way', 'Meilleur moyen'].map(normalize);

export function isBestWay(
  code: string | null | undefined,
  name?: string | null | undefined
): boolean {
  const normalizedCode = normalize(code);
  const normalizedName = normalize(name);

  return (
    normalizedCode.includes('BESTWAY') ||
    BEST_WAY_NAME_VARIANTS.includes(normalizedName)
  );
}

export function isMailroom(
  code: string | null | undefined,
  name?: string | null | undefined
): boolean {
  const normalizedCode = normalize(code);
  const normalizedName = normalize(name);

  return (
    normalizedCode.includes('MAILROOM') || normalizedName.includes('MAILROOM')
  );
}

@Component({
  selector: 'atg-checkout-delivery-mode',
  templateUrl: './atg-checkout-delivery-mode.component.html',
  styleUrls: ['./atg-checkout-delivery-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AtgCheckoutDeliveryModeComponent
  extends CheckoutDeliveryModeComponent
  implements OnInit, OnDestroy {
  collectForm: UntypedFormGroup;
  selectedCode: string | null = null;
  shippingMethods: { code: string; name: string; collectRequired: boolean }[] =
    [];

  private subscriptions = new Subscription();
  isLoading = false;

  constructor(
    protected override fb: UntypedFormBuilder,
    protected override checkoutConfigService: CheckoutConfigService,
    protected override activatedRoute: ActivatedRoute,
    protected override checkoutStepService: CheckoutStepService,
    protected override checkoutDeliveryModesFacade: CheckoutDeliveryModesFacade,
    protected override activeCartFacade: ActiveCartFacade,
    protected override globalMessageService: GlobalMessageService,
    private draftOrdersAdapter: KnBrDraftOrdersAdapter,
    private deliveryAdapter: AtgCheckoutDeliveryAdapter,
    private userAccountFacade: UserAccountFacade,
    private cdr: ChangeDetectorRef
  ) {
    super(
      fb,
      checkoutConfigService,
      activatedRoute,
      checkoutStepService,
      checkoutDeliveryModesFacade,
      activeCartFacade
    );

    this.collectForm = this.fb.group({});
  }

  override changeMode(code: string): void {
    this.selectedCode = code;
    this.updateCollectControls(code);
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest({
        user: this.userAccountFacade.get(),
        res: this.draftOrdersAdapter.getDeliveryModes(),
      })
        .pipe(
          map(({ user, res }) => {
            const isSampleAccount = !!(user as { isSampleAccount?: boolean })
              ?.isSampleAccount;

            return res.deliveryModes
              .filter(
                (m: { code: string; name: string }) =>
                  !isSampleAccount || !isMailroom(m.code, m.name)
              )
              .map(
                (m: {
                  code: string;
                  name: string;
                  collectRequired?: boolean;
                }) => ({
                  code: m.code,
                  name: m.name,
                  collectRequired:
                    isBestWay(m.code, m.name) || isMailroom(m.code, m.name)
                      ? false
                      : m.collectRequired ?? true,
                })
              );
          })
        )
        .subscribe((methods) => {
          this.shippingMethods = methods;

          const controls = methods.reduce(
            (acc, m) => ({
              ...acc,
              [m.code]: [{ value: '', disabled: true }],
            }),
            {}
          );

          this.collectForm = this.fb.group(controls);
          this.cdr.markForCheck();
        })
    );
  }

  private updateCollectControls(selectedCode: string): void {
    this.shippingMethods.forEach((m) => {
      const ctrl = this.collectForm.get(m.code);

      if (!ctrl) return;

      if (m.code === selectedCode) {
        ctrl.enable();
        ctrl.setValue('');
        ctrl.markAsUntouched();
        ctrl.markAsPristine();

        if (m.collectRequired) {
          const validators = isFedexGround(m.code, m.name)
            ? FEDEX_GROUND_VALIDATORS
            : DEFAULT_COLLECT_VALIDATORS;
          ctrl.setValidators(validators);
        } else {
          ctrl.clearValidators();
          ctrl.setValue('');
          ctrl.disable();
        }
      } else {
        ctrl.clearValidators();
        ctrl.setValue('');
        ctrl.markAsUntouched();
        ctrl.markAsPristine();
        ctrl.disable();
      }

      ctrl.updateValueAndValidity();
    });
  }

  get collectNumberInvalid(): boolean {
    const selectedCode = this.mode.get('deliveryModeId')?.value;

    if (!selectedCode) return false;

    const method = this.shippingMethods.find(
      (m) => m.code === selectedCode
    );

    if (!method?.collectRequired) return false;

    return this.collectForm.get(selectedCode)?.invalid ?? false;
  }

  getCollectControl(code: string): AbstractControl {
    return this.collectForm.get(code)!;
  }

  getCollectPlaceholder(code: string): string {
    return 'Collect Number';
  }

  isFedexGround(code: string, name?: string): boolean {
    return isFedexGround(code, name);
  }

  override next(): void {
    if (
      this.mode.invalid ||
      this.collectNumberInvalid ||
      this.isLoading
    ) {
      return;
    }

    this.isLoading = true;

    const selectedCode: string =
      this.mode.get('deliveryModeId')!.value;

    const collectNumber: string =
      this.collectForm.get(selectedCode)?.value ?? '';

    const flow$ = this.deliveryAdapter
      .setModeWithCollect(selectedCode, collectNumber || undefined)
      .pipe(
        take(1),
        tap(() => {
          this.checkoutDeliveryModesFacade.setDeliveryMode(selectedCode);
        }),
        switchMap(() =>
          this.checkoutDeliveryModesFacade
            .getSelectedDeliveryModeState()
            .pipe(
              filter(
                (state) =>
                  !!state &&
                  !state.loading &&
                  state.data?.code === selectedCode
              ),
              take(1)
            )
        ),
        finalize(() => {
          this.isLoading = false;
        })
      );

    this.subscriptions.add(
      flow$.subscribe({
        next: () => {
          this.checkoutStepService.next(this.activatedRoute);
        },
        error: () => {
          this.onError();
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
